import { Logotype } from './Logotype.tsx';
import { useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { clearTenantSlug, isTenantHostLocked, useTenantSlug } from '../lib/tenant.ts';
import { siteOrigin } from '../lib/hosts.ts';
import { TenantEntry } from './TenantEntry.tsx';
import { useBranding } from '../lib/branding.ts';

/**
 * 로그인·초대·재설정 화면 공통 프레임 (CR-013).
 * 왼쪽은 브랜드 패널, 오른쪽은 폼 카드. 조직(테넌트)이 정해지지 않았으면 페이지 내용 대신 조직 ID 입력 단계를 보인다.
 */
export type AuthSteps = { total: number; current: number };

export function AuthShell({ title, subtitle, children, footer, steps, lockTenant = false, noTenant = false, below }: {
  title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode; steps?: AuthSteps;
  /** 폼 카드 아래 부가 상자 (CR-030 로그인 화면 공지). 조직 입력 단계에서도 보인다 */
  below?: ReactNode;
  /** MFA 처럼 세션이 이미 시작된 화면에서는 조직 변경 링크를 감춘다 */
  lockTenant?: boolean;
  /** CR-067: 조직이 정해지기 전의 계정 단계(이메일 로그인·조직 선택). 조직 입력 단계와 조직 칩을 보이지 않는다 */
  noTenant?: boolean;
}) {
  const slug = useTenantSlug();
  const gated = !slug && !noTenant;
  // 단계 전환 방향: 단계 번호가 커지면 오른쪽에서, 작아지면(변경·뒤로) 왼쪽에서 들어온다. 조직 입력은 항상 1단계.
  const stepNo = gated ? 1 : (steps?.current ?? 1);
  const [track, setTrack] = useState({ step: stepNo, dir: 'auth-step-fwd' });
  if (track.step !== stepNo) setTrack({ step: stepNo, dir: stepNo < track.step ? 'auth-step-back' : 'auth-step-fwd' });
  const contentKey = gated ? 'tenant' : title;
  return (
    <div className="min-h-screen grid grid-cols-[minmax(440px,5fr)_7fr] bg-slate-50">
      <BrandPanel />
      <main className="flex items-center justify-center px-8 py-12">
        <div className="w-[420px]">
          <section className="auth-card-in bg-white border border-slate-200 shadow-[0_12px_40px_-12px_rgba(2,6,23,0.12)] px-9 pt-8 pb-9 overflow-hidden">
            {steps && <StepBar total={steps.total} current={stepNo} />}
            <div key={contentKey} className={track.dir}>
              {gated ? <TenantEntry /> : (
                <>
                  <header className="mb-6">
                    <h1 className="text-[22px]! font-semibold! tracking-tight text-slate-900 leading-tight">{title}</h1>
                    {subtitle && <p className="mt-1.5 text-[13px] text-slate-500 leading-relaxed">{subtitle}</p>}
                    {!noTenant && slug && <TenantChip slug={slug} locked={lockTenant || isTenantHostLocked()} />}
                  </header>
                  {children}
                </>
              )}
            </div>
          </section>
          {below}
          {footer && !gated && <div className="mt-5 text-center text-[12.5px] text-slate-500 [&_a]:text-accent-strong [&_a:hover]:underline [&_button]:text-accent-strong [&_button:hover]:underline">{footer}</div>}
          <p className="mt-8 text-center text-[11.5px] text-slate-400">문제가 있으면 조직의 정보보호 관리자에게 문의하세요. · <a href={siteOrigin()} className="hover:underline hover:text-slate-600">서비스 소개</a></p>
        </div>
      </main>
    </div>
  );
}

function BrandPanel() {
  const b = useBranding();
  return (
    <aside className="relative overflow-hidden text-white bg-[linear-gradient(150deg,#0c4a6e_0%,#082f49_55%,#0f172a_100%)] flex flex-col justify-between px-14 py-12">
      {/* 장식: 얇은 격자 + 큰 사각 프레임 (라운드 없음) */}
      <div aria-hidden className="absolute inset-0 opacity-[0.07] bg-[linear-gradient(#fff_1px,transparent_1px),linear-gradient(90deg,#fff_1px,transparent_1px)] bg-[size:48px_48px]" />
      <div aria-hidden className="absolute -right-24 -bottom-24 w-[420px] h-[420px] border border-sky-300/20" />
      <div aria-hidden className="absolute -right-8 -bottom-8 w-[300px] h-[300px] border border-sky-300/30" />
      <div aria-hidden className="absolute right-24 bottom-24 w-[120px] h-[120px] bg-accent/25" />

      <div className="relative auth-brand-in">
        {b?.logoUrl ? <div className="flex items-center gap-4"><img src={b.logoUrl} alt={b.displayName ?? b.tenantName} className="h-[56px] max-w-[260px] w-auto object-contain bg-white/90 p-2" draggable={false} /><div><div className="text-[18px] font-semibold">{b.displayName ?? b.tenantName}</div><div className="text-[11px] text-sky-200/70">powered by PRISM</div></div></div>
          : <Logotype size="lg" className="text-white" />}
      </div>

      {/* 2026-09-13: 가치 제안·포인트 문구 삭제(사용자 지시). 로고타입·규제 태그·저작권만 둔다 */}
      <div className="relative flex items-end justify-between text-[11.5px] text-sky-200/60 auth-brand-in" style={{ animationDelay: '.24s' }}>
        <div className="flex gap-1.5">
          {['ISMS-P', '개인정보보호법', 'ISO 27001'].map((t) => <span key={t} className="border border-sky-200/25 px-2 py-0.5">{t}</span>)}
        </div>
        <div>© 2026 PRISM</div>
      </div>
    </aside>
  );
}

function StepBar({ total, current }: AuthSteps) {
  return (
    <div className="flex items-center gap-1.5 mb-6" aria-label={`${total}단계 중 ${current}단계`}>
      {Array.from({ length: total }, (_, i) => <span key={i} className={`auth-step-seg h-[3px] flex-1 ${i < current ? 'on' : ''}`} style={{ transitionDelay: i < current ? `${i * 60}ms` : '0ms' }} />)}
      <span className="ml-2 text-[11px] text-slate-400 tabular-nums">{current}/{total}</span>
    </div>
  );
}

function TenantChip({ slug, locked }: { slug: string; locked: boolean }) {
  return (
    <div className="mt-3 inline-flex items-center gap-2 border border-slate-200 bg-slate-50 pl-2 pr-1 h-6 text-[12px] text-slate-600">
      <span className="inline-block w-1.5 h-1.5 bg-emerald-500" />
      <span>조직</span>
      <span className="font-mono font-semibold text-slate-900">{slug}</span>
      {!locked && <button type="button" onClick={clearTenantSlug} className="ml-1 px-1.5 h-5 text-[11px] text-slate-500 hover:text-accent-strong hover:bg-white">변경</button>}
    </div>
  );
}

/* ── 폼 요소 ── */

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <label className="block mb-4">
      <span className="block text-[12.5px] font-medium text-slate-700 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block mt-1.5 text-[11.5px] text-slate-400 leading-snug">{hint}</span>}
    </label>
  );
}
export const inputCls = 'w-full h-10 border border-slate-300 bg-white px-3 text-[13.5px] text-slate-900 placeholder:text-slate-400 transition focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted disabled:bg-slate-50 disabled:text-slate-500';
export const btnCls = 'w-full h-10 inline-flex items-center justify-center gap-2 bg-accent text-white text-[13.5px] font-semibold transition hover:bg-accent-strong focus:outline-none focus:ring-2 focus:ring-accent-muted disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * 인증 화면 제출 버튼. `busy` 면 스피너와 진행 문구, `done` 이면 초록 배경에 체크 표시로 바뀐다(다음 화면으로 넘어가기 직전 완료 피드백).
 */
export function AuthButton({ busy, done, busyText, doneText, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & {
  busy?: boolean; done?: boolean; busyText?: ReactNode; doneText?: ReactNode;
}) {
  return (
    <button {...rest} disabled={busy || done || rest.disabled} aria-busy={busy || undefined}
      className={`${btnCls} ${done ? 'bg-emerald-600! hover:bg-emerald-600! disabled:opacity-100! disabled:cursor-default!' : ''} ${className ?? ''}`}>
      {done ? <><CheckIcon />{doneText ?? children}</> : busy ? <><span className="spinner" />{busyText ?? children}</> : children}
    </button>
  );
}

function CheckIcon() {
  return (
    <svg className="auth-check" width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="square" aria-hidden>
      <path d="M3 8.5l3.2 3.2L13 4.8" />
    </svg>
  );
}

/** 완료 피드백(체크 표시)을 보여줄 짧은 시간 */
export const DONE_MS = 450;
export const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** 비밀번호 입력: 표시/숨김 토글 포함 */
export function PasswordInput(props: InputHTMLAttributes<HTMLInputElement>) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input {...props} type={show ? 'text' : 'password'} className={`${inputCls} pr-14 ${props.className ?? ''}`} />
      <button type="button" tabIndex={-1} onClick={() => setShow(!show)} className="absolute right-1 top-1 h-8 px-2 text-[11.5px] text-slate-500 hover:text-accent-strong hover:bg-slate-50">{show ? '숨김' : '표시'}</button>
    </div>
  );
}

export function ErrorText({ error }: { error: unknown }) {
  if (!error) return null;
  const msg = error instanceof Error ? error.message : String(error);
  return <div role="alert" className="auth-shake mt-3 border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700 whitespace-pre-line leading-snug">{msg}</div>;
}
